import { useEffect, useMemo, useState } from 'react'

import { apiGetOrderHistory } from '@src/api/order.api'
import OrderHistoryTableComponent, { OrderItemTableRow } from '@src/components/OrderHistoryTable'
import SearchTokenInput from '@src/components/SearchTokenInput'
import ConnectButton from '@src/components/buttons/ConnectButton'
import NoWalletIcon from '@src/components/icons/NoWallet'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { IOrderHistoryItem } from '@src/types/order'
import { IToken } from '@src/types/token'
import { useConfigStore } from '@src/zustand_stores'
import { useTokensStore } from '@src/zustand_stores'

const OrdersPage = () => {
    const {
        isEVMConnected,
        evmWalletAddress,
        isSolanaConnected,
        solanaWalletAddress,
        isTronConnected,
        tronWalletAddress,
    } = useWalletConnections()

    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const { tokens: zustandTokens } = useTokensStore()

    const [search, setSearch] = useState('')
    const [orderHistoryTableData, setOrderHistoryTableData] = useState<OrderItemTableRow[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const connectedWallets = useMemo(() => {
        const wallets = []
        if (isEVMConnected && evmWalletAddress) wallets.push(evmWalletAddress)
        if (isSolanaConnected && solanaWalletAddress) wallets.push(solanaWalletAddress)
        if (isTronConnected && tronWalletAddress) wallets.push(tronWalletAddress)
        return wallets
    }, [isEVMConnected, evmWalletAddress, isSolanaConnected, solanaWalletAddress, isTronConnected, tronWalletAddress])

    const isWalletConnected = connectedWallets.length > 0

    const getChainIdFromName = (name: string) => {
        // Normalize the name by removing spaces and converting to lowercase
        const normalizedName = name.toLowerCase().replace(/\s+/g, '')

        return Object.keys(availableNetworks).find((chainId: string) => {
            const networkName = availableNetworks[chainId].name.toLowerCase().replace(/\s+/g, '')
            return networkName === normalizedName
        })
    }

    const getTokenFromAddress = (address: string) => {
        try {
            return zustandTokens.find((token: IToken) => token.address?.toLowerCase() === address.toLowerCase())
        } catch (error) {
            console.error('Error in getTokenFromAddress:', error)
            return null
        }
    }

    const convertToTableRows = (data: IOrderHistoryItem[], walletAddress: string) => {
        const orderTableRows: OrderItemTableRow[] = []

        for (const orderItem of data) {
            const sellToken = getTokenFromAddress(orderItem.src_asset_address)
            const buyToken = getTokenFromAddress(orderItem.dst_asset_address)
            const originChainId = getChainIdFromName(orderItem.src_chain)
            const destChainId = getChainIdFromName(orderItem.dst_chain)

            if (!sellToken || !buyToken || !originChainId || !destChainId) {
                continue
            }

            const tableRow: OrderItemTableRow = {
                sellToken,
                buyToken,
                sellQuantity: orderItem.src_amount,
                buyQuantity: orderItem.dst_amount,
                originChainId,
                destChainId,
                originChainTxHash: orderItem.place_tx,
                destChainTxHash: orderItem.fill_tx,
                created_at: orderItem.created_at,
                filled_at: orderItem.filled_at,
                expires_at: orderItem.expires_at,
                completed: orderItem.completed,
                eta: orderItem.eta,
                order_type: orderItem.order_type as 'order_type' | 'cctp',
                walletAddress,
            }
            orderTableRows.push(tableRow)
        }

        return orderTableRows
    }

    const getOrderHistory = async () => {
        setIsLoading(true)
        try {
            const allOrderPromises = connectedWallets.map(async wallet => {
                try {
                    const data = await apiGetOrderHistory(wallet)
                    return convertToTableRows(data || [], wallet)
                } catch (err) {
                    console.error(`Error fetching orders for wallet ${wallet}:`, err)
                    return []
                }
            })

            const allOrders = await Promise.all(allOrderPromises)
            const flattenedOrders = allOrders.flat()

            // Sort by created_at date, most recent first
            const sortedOrders = flattenedOrders.sort((a, b) => {
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            })

            setOrderHistoryTableData(sortedOrders)
        } catch (err) {
            console.error('Error fetching orders:', err)
            setOrderHistoryTableData([])
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (isWalletConnected) {
            getOrderHistory()
        } else {
            setOrderHistoryTableData([])
            setIsLoading(false)
        }
    }, [connectedWallets.join(',')])

    const searchedOrderHistory = useMemo(() => {
        if (!search) {
            return orderHistoryTableData
        }
        const searchLowerCase = search.toLowerCase()
        const filteredResults = orderHistoryTableData.filter(
            order =>
                availableNetworks[order.originChainId]?.name?.toLowerCase()?.includes(searchLowerCase) ||
                availableNetworks[order.destChainId]?.name?.toLowerCase()?.includes(searchLowerCase) ||
                order.originChainTxHash.toLowerCase().includes(search) ||
                order.destChainTxHash?.toLowerCase().includes(search) ||
                order.buyToken.name.toLowerCase().includes(searchLowerCase) ||
                order.sellToken.name.toLowerCase().includes(searchLowerCase) ||
                order.walletAddress?.toLowerCase().includes(searchLowerCase)
        )
        return filteredResults
    }, [search, orderHistoryTableData])

    // Memoize the order counts to avoid recalculating on every render
    const orderCounts = useMemo(() => {
        const counts = {
            pending: 0,
            expired: 0,
        }

        const currentTime = new Date().getTime()

        orderHistoryTableData.forEach(item => {
            // Skip completed orders
            if (item.destChainTxHash) return

            const expirationTime = item.expires_at ? new Date(item.expires_at).getTime() : 0

            if (expirationTime && currentTime > expirationTime) {
                counts.expired++
            } else {
                counts.pending++
            }
        })

        return counts
    }, [orderHistoryTableData])

    return (
        <>
            {!isWalletConnected ? (
                <div className='container flex flex-grow flex-col items-center justify-center'>
                    <div className='flex flex-col items-center justify-center space-y-[21px]'>
                        <NoWalletIcon />
                        <div className='space-y-3 text-center'>
                            <p className='text-xl font-semibold'>No wallet connected</p>
                            <span className='text-xs font-medium'>Connect a wallet to see order history</span>
                        </div>
                        <ConnectButton />
                    </div>
                </div>
            ) : (
                <>
                    <div className='container flex flex-col space-y-7 py-7'>
                        <div className='flex flex-col space-y-8'>
                            <div className='flex flex-col space-y-1'>
                                <span className='text-sm text-gray-400'>Connected Addresses</span>
                                <div className='flex flex-col space-y-2'>
                                    {connectedWallets.map(wallet => (
                                        <span key={wallet} className='font-mono text-2xl font-bold text-white'>
                                            {wallet}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div className='flex items-center gap-16'>
                                <div className='flex flex-col'>
                                    <span className='text-sm text-gray-400'>Total Orders</span>
                                    <div className='flex items-baseline gap-2'>
                                        <span className='text-[40px] font-medium leading-none text-white'>
                                            {isLoading ? (
                                                <div className='mt-2 h-8 w-24 animate-pulse rounded bg-gray-400' />
                                            ) : (
                                                orderHistoryTableData.length || 0
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className='h-12 w-[2px] bg-gray-700/50'></div>
                                <div className='flex flex-col'>
                                    <span className='text-sm text-gray-400'>Pending Orders</span>
                                    <div className='flex items-baseline gap-2'>
                                        <span className='text-[40px] font-medium leading-none text-white'>
                                            {isLoading ? (
                                                <div className='mt-2 h-8 w-24 animate-pulse rounded bg-gray-400' />
                                            ) : (
                                                orderCounts.pending
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className='h-12 w-[2px] bg-gray-700/50'></div>
                                <div className='flex flex-col'>
                                    <span className='text-sm text-gray-400'>Expired Orders</span>
                                    <div className='flex items-baseline gap-2'>
                                        <span className='text-[40px] font-medium leading-none text-white'>
                                            {isLoading ? (
                                                <div className='mt-2 h-8 w-24 animate-pulse rounded bg-gray-400' />
                                            ) : (
                                                orderCounts.expired
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='flex flex-row justify-between'>
                            <h2 className='text-[32px] font-bold'>&nbsp;</h2>
                            <SearchTokenInput className='w-[400px] max-w-full' onValueChange={val => setSearch(val)} />
                        </div>
                    </div>
                    <div className='container flex flex-grow flex-col items-center'>
                        <OrderHistoryTableComponent
                            data={searchedOrderHistory}
                            loadingStatus={isLoading}
                            search={search}
                        />
                    </div>
                </>
            )}
        </>
    )
}

export default OrdersPage
